import { useState, useCallback, useMemo, type FormEvent } from "react";
import { Link, useLocation, useSearch } from "wouter";
import { Eye, EyeOff, KeyRound, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { resetPassword } from "@/lib/auth-api";
import { AUTH_STYLES, AuthShell } from "@/components/auth-shell";
import { cn } from "@/lib/utils";

const MIN_LENGTH = 8;
const INVALID_LINK_MESSAGE = "This reset link is invalid or has expired. Please request a new one.";

function passwordStrength(password: string): { label: string; className: string; score: number } {
  let score = 0;
  if (password.length >= MIN_LENGTH) score++;
  if (/[A-Z]/.test(password) && /[a-z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;

  if (password.length === 0) return { label: "", className: "bg-transparent", score: 0 };
  if (score <= 1) return { label: "Weak", className: "bg-destructive", score };
  if (score <= 2) return { label: "Fair", className: "bg-golden-foreground", score };
  if (score === 3) return { label: "Good", className: "bg-sky-foreground", score };
  return { label: "Strong", className: "bg-sage-foreground", score };
}

export default function ResetPasswordPage() {
  const [, setLocation] = useLocation();
  const search = useSearch();
  const token = useMemo(() => new URLSearchParams(search).get("token") ?? "", [search]);

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [message, setMessage] = useState<{ type: "ok" | "err"; text: string } | null>(
    token ? null : { type: "err", text: INVALID_LINK_MESSAGE },
  );

  const strength = passwordStrength(password);

  const onSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      setMessage(null);

      if (!token) {
        setMessage({ type: "err", text: INVALID_LINK_MESSAGE });
        return;
      }
      if (password.length < MIN_LENGTH) {
        setMessage({ type: "err", text: `Password must be at least ${MIN_LENGTH} characters.` });
        return;
      }
      if (password !== confirmPassword) {
        setMessage({ type: "err", text: "Passwords do not match." });
        return;
      }

      setSubmitting(true);
      try {
        const result = await resetPassword(token, password);
        if (!result.ok) {
          setMessage({ type: "err", text: result.error });
          return;
        }
        setDone(true);
        setMessage({ type: "ok", text: "Your password has been updated. Redirecting to sign in…" });
        setTimeout(() => setLocation("/login"), 1800);
      } finally {
        setSubmitting(false);
      }
    },
    [token, password, confirmPassword, setLocation],
  );

  return (
    <AuthShell backHref="/login" backLabel="← Back to sign in">
      <Card className={AUTH_STYLES.card}>
        <CardHeader className={AUTH_STYLES.header}>
          <div className={cn(AUTH_STYLES.badge, "flex items-center justify-center bg-lavender-wash text-primary")}>
            {done ? <CheckCircle2 className="h-7 w-7" /> : <KeyRound className="h-7 w-7" />}
          </div>
          <CardTitle className={AUTH_STYLES.title}>
            {done ? "Password updated" : "Set a new password"}
          </CardTitle>
          <CardDescription className={AUTH_STYLES.subtitle}>
            {done ? "You're all set." : "Choose a new password for your account."}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {message && (
            <div
              role="alert"
              className={
                message.type === "err"
                  ? "text-sm rounded-md border border-destructive/30 bg-destructive/10 text-destructive px-3 py-2"
                  : "flex items-start gap-2 text-sm rounded-md border border-primary/20 bg-primary/5 text-foreground px-3 py-2"
              }
            >
              {message.type === "ok" && <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0 text-primary" />}
              <span>{message.text}</span>
            </div>
          )}

          {!done && token && (
            <form onSubmit={onSubmit} className="space-y-4">
              <div className={AUTH_STYLES.field}>
                <Label htmlFor="new-password">New password</Label>
                <div className="relative">
                  <Input
                    id="new-password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="new-password"
                    required
                    minLength={MIN_LENGTH}
                    value={password}
                    onChange={(ev) => setPassword(ev.target.value)}
                    placeholder="••••••••"
                    className={cn(AUTH_STYLES.input, "pr-10")}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {password.length > 0 && (
                  <div className="space-y-1">
                    <div className="h-1 w-full rounded-full bg-muted overflow-hidden flex gap-0.5">
                      {[0, 1, 2, 3].map((i) => (
                        <div
                          key={i}
                          className={cn(
                            "h-full flex-1 rounded-full transition-colors",
                            i < strength.score ? strength.className : "bg-muted-foreground/15",
                          )}
                        />
                      ))}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {strength.label ? `${strength.label} password · ` : ""}
                      At least {MIN_LENGTH} characters
                    </p>
                  </div>
                )}
              </div>
              <div className={AUTH_STYLES.field}>
                <Label htmlFor="confirm-password">Confirm password</Label>
                <Input
                  id="confirm-password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  required
                  minLength={MIN_LENGTH}
                  value={confirmPassword}
                  onChange={(ev) => setConfirmPassword(ev.target.value)}
                  placeholder="••••••••"
                  className={AUTH_STYLES.input}
                />
              </div>
              <Button type="submit" className={AUTH_STYLES.primary} disabled={submitting}>
                {submitting ? "Updating…" : "Update password"}
              </Button>
            </form>
          )}

          {!done && message?.type === "err" && message.text.includes("invalid or has expired") && (
            <Button variant="outline" className="h-[42px] w-full rounded-md text-sm" onClick={() => setLocation("/login?mode=forgot")}>
              Request a new link
            </Button>
          )}
        </CardContent>
        <CardFooter>
          <Link
            href="/login"
            className={cn(AUTH_STYLES.footerLink, "mx-auto")}
          >
            Back to sign in
          </Link>
        </CardFooter>
      </Card>
    </AuthShell>
  );
}
